/**
 * Moteur de synchronisation Hybride (poste) — SYNC_DESIGN §6.5 à §6.8.
 *
 * Autonome : il ne réutilise PAS `CachedRemoteEntityRepository` ni `SyncCoordinator` (qui lisent d'abord le serveur).
 * Principes :
 *  - la saisie locale ne dépend jamais de ce moteur (I1) : il ne fait que lire la file et écrire des fiches distantes ;
 *  - une modification n'est jamais perdue (I2) : refus définitif -> `rejected` visible ; licence, réseau, session ->
 *    pause SANS toucher à la file ;
 *  - aucune boîte de dialogue bloquante ; les situations à signaler remontent par `onNotice` (toasts discrets) ;
 *  - aucune donnée métier dans les journaux (I9).
 */
import { backoffDelayMs } from './SyncBackoff';
import { defaultTimers, SyncNetworkMonitor, type SyncTimers } from './SyncNetworkMonitor';
import { SyncInitialSync } from './SyncInitialSync';
import {
  SYNC_CLIENT_SCHEMA_VERSION,
  SYNC_LIMITS,
  SYNC_PROTOCOL_VERSION,
  compareVersions,
  parseCursor,
  toWireOp,
  type SyncOpResult,
  type SyncPushResponse,
  type SyncStatusResponse,
} from './SyncProtocol';
import { SyncRequestError, type SyncApiPort, type SyncOutboxPort } from './SyncPorts';
import type { SyncLocalState, SyncRetryItem, SyncWireEntry } from './SyncBridgeTypes';

export type SyncEngineState =
  | 'disabled'
  | 'idle'
  | 'syncing'
  | 'offline'
  | 'needs_login'
  | 'read_only'
  | 'paused_upgrade'
  | 'revoked'
  | 'error';

export type SyncTrigger = 'start' | 'timer' | 'local' | 'online' | 'focus' | 'manual' | 'login' | 'resume' | 'close';

export interface SyncStatus {
  state: SyncEngineState;
  phase: SyncLocalState['phase'];
  pendingCount: number;
  retryCount: number;
  rejectedCount: number;
  /** Éléments « À examiner » = entrées refusées + copies mises de côté. */
  attentionCount: number;
  oldestPendingAt: string | null;
  lastSuccessAt: string | null;
  lastPullAt: string | null;
  readOnlyReason: string | null;
  offlineSince: string | null;
  lastErrorCode: string | null;
  /** Jours sans synchronisation réussie quand il reste des modifications en attente (0 sinon). */
  blockedDays: number;
  /** Avancement du premier envoi / téléchargement initial. */
  progress: { done: number; total: number } | null;
}

export type SyncNotice =
  | { kind: 'merged_from_other_device'; count: number }
  | { kind: 'entry_rejected'; code: string; resource: string; message: string }
  | { kind: 'bulletin_locked'; message: string }
  | { kind: 'initial_summary'; downloaded: number; replaced: number; uploaded: number; identical: number }
  | { kind: 'rebootstrap'; reason: 'cursor_expired' | 'epoch_changed' };

export interface CycleSummary {
  pushed: number;
  rejected: number;
  retried: number;
  pulled: number;
  skipped: boolean;
  state: SyncEngineState;
}

export interface HybridSyncEngineDeps {
  tenantId: string;
  api: SyncApiPort;
  outbox: SyncOutboxPort;
  appVersion: string;
  deviceLabel?: () => Promise<string> | string;
  now?: () => number;
  random?: () => number;
  timers?: SyncTimers;
  /** Verrou de cycle inter-fenêtres (`navigator.locks`) ; sans lui, le drapeau en mémoire suffit. */
  withLock?: <T>(fn: () => Promise<T>) => Promise<T>;
  /** Cède la main à l'interface entre deux paquets de fiches. */
  yieldToUi?: () => Promise<void>;
  onStatus?: (status: SyncStatus) => void;
  onNotice?: (notice: SyncNotice) => void;
  /** Fiches écrites localement depuis le serveur (rafraîchissement des vues ouvertes). */
  onRemoteApplied?: (resources: Record<string, number>) => void;
  /** Identité de la session telle que le serveur la voit (rôle courant) : le cache d'identité local la suit. */
  onServerSession?: (session: { user_id: string; role: string }) => void;
  /** Licence : lecture seule confirmée par le serveur (true) ou levée (false). */
  onReadOnlyChange?: (readOnly: boolean, reason: string | null) => void;
}

const PERIODIC_MS = 60_000;
const READ_ONLY_PROBE_MS = 5 * 60_000;
const LOCAL_DEBOUNCE_MS = 2_000;
const LOCAL_DEBOUNCE_MAX_MS = 10_000;
const QUICK_RETRY_MS = 2_000;
const MAX_PUSH_ITERATIONS = 2_000;
const BLOCKED_AFTER_MS = 24 * 3_600_000;

const SESSION_CODES = new Set([
  'session_invalid',
  'user_token_required',
  'installation_invalid',
  'installation_token_required',
  'installation_expired',
  'account_locked',
]);
/** Codes où un renouvellement de session a une chance d'aboutir (les autres demandent une action de l'utilisateur). */
const REFRESHABLE_CODES = new Set(['session_invalid', 'user_token_required']);

const MESSAGES_FR: Record<string, string> = {
  payslip_locked: 'Bulletin validé ou payé : votre modification n’a pas été appliquée.',
  insufficient_role: 'Votre rôle ne permet plus cette modification.',
  forbidden_role: 'Votre rôle ne permet plus cette modification.',
  module_not_licensed: 'Ce module n’est pas inclus dans la licence.',
  salary_limit_reached: 'Le nombre maximal de salariés de la licence est atteint.',
  payload_invalide: 'Le contenu de cette fiche a été refusé par le serveur.',
  cnss_cotisation_invalide: 'Les cotisations CNSS de ce bulletin ont été refusées par le serveur.',
  delete_not_allowed: 'Cette suppression n’est pas autorisée.',
  author_invalid: 'L’auteur de la modification n’est plus valable sur le serveur.',
  batch_invalid: 'Cette modification est mal formée et n’a pas pu être envoyée.',
  user_limit_reached: 'Le nombre maximal d’utilisateurs de la licence est atteint.',
};

export function rejectionMessageFr(code: string, serverMessage?: string): string {
  return MESSAGES_FR[code] ?? (serverMessage && serverMessage.trim() ? serverMessage.slice(0, 300) : 'Modification refusée par le serveur.');
}

type ErrorDisposition = 'abort' | 'readonly' | 'isolate' | 'retry_now' | 'rebootstrap_cursor' | 'rebootstrap_epoch';

export class HybridSyncEngine {
  private state: SyncEngineState = 'idle';
  private started = false;
  private running = false;
  private currentCycle: Promise<void> = Promise.resolve();
  private rerunTrigger: SyncTrigger | null = null;
  private timerHandle: unknown = null;
  private debounceHandle: unknown = null;
  private firstWriteAt = 0;
  private consecutiveFailures = 0;
  private nextAllowedAt = 0;
  private readOnlyReason: string | null = null;
  private lastErrorCode: string | null = null;
  private lastSuccessMs: number | null = null;
  private progress: { done: number; total: number } | null = null;
  private counters: SyncLocalState | null = null;
  private attentionCount = 0;
  private readonly monitor: SyncNetworkMonitor;
  private readonly timers: SyncTimers;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly initial: SyncInitialSync;

  constructor(private readonly deps: HybridSyncEngineDeps) {
    this.timers = deps.timers ?? defaultTimers;
    this.now = deps.now ?? Date.now;
    this.random = deps.random ?? Math.random;
    this.monitor = new SyncNetworkMonitor({
      probe: () => this.probeReachable(),
      timers: this.timers,
      random: this.random,
      now: this.now,
      onChange: (online) => this.onNetworkChange(online),
    });
    this.initial = new SyncInitialSync({
      api: deps.api,
      outbox: deps.outbox,
      yieldToUi: deps.yieldToUi,
      onProgress: (p) => {
        this.progress = p;
        this.publish();
      },
    });
  }

  // --- cycle de vie -------------------------------------------------------------------------

  /** Démarre le moteur (après ouverture de session). Idempotent. */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    // Une entrée « en cours d'envoi » à la relance a un résultat inconnu : rejeu idempotent.
    await this.deps.outbox.resetSending();
    this.state = 'idle';
    await this.refreshCounters();
    this.publish();
    void this.runCycle('start');
  }

  stop(): void {
    this.started = false;
    this.clearTimers();
    this.monitor.dispose();
    this.state = 'disabled';
    this.publish();
  }

  getStatus(): SyncStatus {
    return this.buildStatus();
  }

  // --- déclencheurs ---------------------------------------------------------------------------

  /** Bouton « Synchroniser maintenant » : contourne les pauses de reprise. */
  syncNow(): Promise<CycleSummary> {
    return this.runCycle('manual');
  }

  /** Une écriture locale vient d'avoir lieu : compteur à jour tout de suite, envoi après 2 s (10 s au plus). */
  notifyLocalWrite(): void {
    void this.refreshCounters().then(() => this.publish());
    if (!this.started) return;
    const t = this.now();
    if (this.debounceHandle === null) this.firstWriteAt = t;
    else this.timers.clear(this.debounceHandle);
    const waited = t - this.firstWriteAt;
    const delay = Math.max(0, Math.min(LOCAL_DEBOUNCE_MS, LOCAL_DEBOUNCE_MAX_MS - waited));
    this.debounceHandle = this.timers.set(() => {
      this.debounceHandle = null;
      void this.runCycle('local');
    }, delay);
  }

  /** Événement `online` du navigateur, retour de focus : sonde immédiate puis cycle. */
  async notifyOnline(trigger: 'online' | 'focus' = 'online'): Promise<void> {
    if (!this.started) return;
    if (this.state === 'offline') {
      const ok = await this.monitor.probeNow();
      if (!ok) return;
    }
    await this.runCycle(trigger);
  }

  notifyBrowserOffline(): void {
    if (!this.started) return;
    this.monitor.browserOffline();
  }

  /** Session ouverte ou renouvelée par l'utilisateur. */
  notifyLogin(): Promise<CycleSummary> {
    return this.runCycle('login');
  }

  /** Fermeture de l'application : envoi « au mieux », limité à 5 s. */
  async flushOnClose(budgetMs = 5_000): Promise<void> {
    if (!this.started || this.state === 'offline') return;
    await Promise.race([
      this.runCycle('close'),
      new Promise<void>((resolve) => {
        this.timers.set(resolve, budgetMs);
      }),
    ]);
  }

  // --- cycle ------------------------------------------------------------------------------------

  private canRun(trigger: SyncTrigger): boolean {
    if (!this.started) return false;
    const forced = trigger === 'manual' || trigger === 'login' || trigger === 'resume';
    switch (this.state) {
      case 'revoked':
      case 'disabled':
      case 'paused_upgrade':
        return forced;
      case 'needs_login':
        return forced;
      case 'offline':
        return trigger === 'online' || trigger === 'manual' || trigger === 'focus' || trigger === 'start';
      default:
        break;
    }
    if (!forced && trigger !== 'online' && this.now() < this.nextAllowedAt) return false;
    return true;
  }

  async runCycle(trigger: SyncTrigger): Promise<CycleSummary> {
    if (!this.canRun(trigger)) return { ...emptySummary(this.state), skipped: true };
    if (this.running) {
      if (trigger === 'manual' || trigger === 'login') {
        // Demande explicite de l'utilisateur : on attend la fin du cycle en cours, puis on en lance un autre.
        await this.currentCycle;
        return this.runCycle(trigger);
      }
      this.rerunTrigger = trigger;
      return { ...emptySummary(this.state), skipped: true };
    }
    this.running = true;
    let release: () => void = () => undefined;
    this.currentCycle = new Promise<void>((resolve) => {
      release = resolve;
    });
    this.clearPeriodic();
    const lock = this.deps.withLock ?? (<T>(fn: () => Promise<T>) => fn());
    const t0 = this.now();
    let result: CycleSummary = emptySummary(this.state);
    try {
      result = await lock(() => this.cycle());
    } catch (err) {
      // Garde-fou : aucune exception ne doit tuer le moteur ni toucher à la file.
      this.lastErrorCode = 'internal_error';
      if (this.state === 'syncing') this.state = 'error';
      await this.safeEvent({ type: 'sync_error', code: 'internal_error' });
      void err;
    } finally {
      this.running = false;
      this.progress = null;
      release();
    }
    await this.refreshCounters();
    await this.safeEvent({ type: 'sync_cycle', n: result.pushed + result.pulled, durationMs: this.now() - t0 });
    this.publish();
    this.schedulePeriodic();
    const again = this.rerunTrigger;
    this.rerunTrigger = null;
    if (again && this.state !== 'offline') void this.runCycle(again);
    return result;
  }

  private async cycle(): Promise<CycleSummary> {
    const summary = emptySummary(this.state);
    const previous = this.state;
    if (this.state !== 'offline' && this.state !== 'read_only') this.state = 'syncing';
    this.publish();

    // 1. Sonde : réseau, licence, consentement, version minimale.
    let status: SyncStatusResponse;
    try {
      status = await this.deps.api.status();
    } catch (err) {
      await this.onRequestError(err, { inflight: [], where: 'status' });
      summary.state = this.state;
      return summary;
    }
    this.noteSuccessfulRequest(previous);
    if (this.isState('offline', 'error', 'needs_login')) this.state = 'syncing';
    if (!status.sync_enabled) {
      // Premier démarrage après le consentement donné sur ce poste (éventuellement hors ligne) : on l'enregistre
      // côté serveur. Une synchro déjà en service que le serveur déclare désactivée (retrait du consentement
      // depuis ailleurs) n'est JAMAIS réactivée automatiquement.
      const local = await this.deps.outbox.getState();
      if (local.enabled && local.consentVersion && local.phase === 'uninitialized') {
        try {
          await this.deps.api.enable(local.consentVersion);
          status = await this.deps.api.status();
        } catch (err) {
          const disposition = await this.onRequestError(err, { inflight: [], where: 'status' });
          if (disposition === 'abort') {
            summary.state = this.state;
            return summary;
          }
        }
      }
    }
    if (!status.sync_enabled) {
      await this.enterState('disabled', 'sync_not_enabled');
      summary.state = this.state;
      return summary;
    }
    if (this.needsUpgrade(status)) {
      await this.enterState('paused_upgrade', 'client_upgrade_required');
      summary.state = this.state;
      return summary;
    }
    if (status.session) this.deps.onServerSession?.({ user_id: status.session.user_id, role: status.session.role });
    const readOnly = status.mode === 'read_only';
    await this.applyReadOnly(readOnly, status.license.denial_code ?? 'license_inactive');
    if (!readOnly && (this.state === 'syncing' || this.state === 'read_only')) this.state = 'syncing';

    // 2. Premier envoi / téléchargement initial / époque changée.
    let local = await this.deps.outbox.getState();
    if (local.phase !== 'live') {
      const outcome = await this.runInitial(status, readOnly, summary);
      if (outcome === 'abort') {
        summary.state = this.state;
        return summary;
      }
      local = await this.deps.outbox.getState();
    } else if (local.epoch && local.epoch !== status.epoch) {
      const outcome = await this.rebootstrap('epoch_changed', status);
      if (outcome === 'abort') {
        summary.state = this.state;
        return summary;
      }
    }

    // 3. Envoi puis réception, jusqu'à quiescence (au plus quelques tours si des écritures arrivent entre-temps).
    for (let round = 0; round < 4; round += 1) {
      if (!this.readOnlyNow()) {
        const pushed = await this.pushLoop(summary);
        if (pushed === 'abort') {
          summary.state = this.state;
          return summary;
        }
      }
      const pulled = await this.pullLoop(summary);
      if (pulled === 'abort') {
        summary.state = this.state;
        return summary;
      }
      const left = await this.deps.outbox.getState();
      const dueSoon = !this.readOnlyNow() && left.pendingCount - left.retryCount > 0;
      if (!dueSoon) break;
    }

    this.consecutiveFailures = 0;
    this.nextAllowedAt = 0;
    this.lastErrorCode = null;
    this.lastSuccessMs = this.now();
    if (!this.readOnlyNow()) this.state = 'idle';
    summary.state = this.state;
    return summary;
  }

  // --- sonde, état, licence -------------------------------------------------------------------

  private async probeReachable(): Promise<boolean> {
    try {
      await this.deps.api.status();
      return true;
    } catch (err) {
      if (err instanceof SyncRequestError && !err.isConnectivity) return true; // le serveur a répondu
      return false;
    }
  }

  private noteSuccessfulRequest(previous: SyncEngineState): void {
    const wasOffline = !this.monitor.online;
    this.monitor.reportSuccess();
    if (wasOffline || previous === 'offline') void this.safeEvent({ type: 'sync_offline_exit' });
    if (previous === 'needs_login') this.lastErrorCode = null;
  }

  private needsUpgrade(status: SyncStatusResponse): boolean {
    if (status.min_client_version && compareVersions(this.deps.appVersion, status.min_client_version) < 0) return true;
    if (status.min_schema_version && SYNC_CLIENT_SCHEMA_VERSION < status.min_schema_version) return true;
    return false;
  }

  private isState(...states: SyncEngineState[]): boolean {
    return states.includes(this.state);
  }

  private readOnlyNow(): boolean {
    return this.state === 'read_only' || this.readOnlyReason !== null;
  }

  private async applyReadOnly(readOnly: boolean, reason: string): Promise<void> {
    if (readOnly) {
      if (this.readOnlyReason === null) {
        this.readOnlyReason = reason;
        await this.safeEvent({ type: 'sync_readonly', code: reason });
        await this.deps.outbox.updateMeta({ mode: 'read_only' });
        this.deps.onReadOnlyChange?.(true, reason);
      }
      this.state = 'read_only';
    } else if (this.readOnlyReason !== null) {
      this.readOnlyReason = null;
      await this.safeEvent({ type: 'sync_resumed' });
      await this.deps.outbox.updateMeta({ mode: 'read_write' });
      this.deps.onReadOnlyChange?.(false, null);
      if (this.state === 'read_only') this.state = 'syncing';
    }
  }

  private async enterState(state: SyncEngineState, code: string): Promise<void> {
    this.state = state;
    this.lastErrorCode = code;
    this.clearTimers();
    await this.safeEvent({ type: 'sync_paused', code });
    await this.deps.outbox.updateMeta({ last_error_code: code, last_error_at: new Date(this.now()).toISOString() });
  }

  private onNetworkChange(online: boolean): void {
    if (online) {
      if (this.state === 'offline') this.state = 'idle';
      void this.safeEvent({ type: 'sync_offline_exit' });
      this.publish();
      if (!this.running) void this.runCycle('online');
    } else {
      this.state = 'offline';
      this.clearPeriodic();
      void this.safeEvent({ type: 'sync_offline_enter' });
      this.publish();
    }
  }

  // --- erreurs de requête ----------------------------------------------------------------------

  private asRequestError(err: unknown): SyncRequestError {
    if (err instanceof SyncRequestError) return err;
    return new SyncRequestError(err instanceof Error ? err.message : 'erreur inconnue', 'protocol');
  }

  /** Remet en `retry` les entrées d'un lot dont le résultat est inconnu. */
  private async releaseInflight(inflight: SyncWireEntry[], code: string, count: boolean, retryAfterMs = 0): Promise<void> {
    if (inflight.length === 0) return;
    const items: SyncRetryItem[] = inflight.map((e) => ({
      op_id: e.op_id,
      delay_ms: count ? backoffDelayMs(e.attempts + 1, this.random, retryAfterMs) : 0,
    }));
    await this.deps.outbox.markRetry(items, code, count);
  }

  private async onRequestError(
    raw: unknown,
    ctx: { inflight: SyncWireEntry[]; where: 'status' | 'push' | 'pull' | 'bootstrap' },
  ): Promise<ErrorDisposition> {
    const err = this.asRequestError(raw);
    const code = err.code ?? '';

    // Réseau coupé, délai dépassé, 502-504 : problème de connexion, jamais de donnée.
    if (err.isConnectivity) {
      await this.releaseInflight(ctx.inflight, err.kind === 'network' ? 'network' : `http_${err.statusCode ?? 'timeout'}`, err.kind !== 'network', err.retryAfterMs ?? 0);
      this.lastErrorCode = err.kind === 'network' ? 'network' : (err.code ?? 'timeout');
      const offline = this.monitor.reportFailure();
      if (offline) {
        this.state = 'offline';
        this.clearPeriodic();
      } else {
        this.state = 'idle';
        this.scheduleQuickRetry();
      }
      return 'abort';
    }

    if (err.statusCode === 401 || SESSION_CODES.has(code)) {
      await this.releaseInflight(ctx.inflight, code || 'session_invalid', false);
      if ((err.statusCode === 401 || REFRESHABLE_CODES.has(code)) && !INSTALLATION_CODES.has(code)) {
        let renewed = false;
        try {
          renewed = await this.deps.api.refreshSession();
        } catch {
          renewed = false;
        }
        if (renewed) return 'retry_now';
      }
      await this.enterState('needs_login', code || 'session_invalid');
      await this.safeEvent({ type: 'sync_needs_login', code: code || 'session_invalid' });
      return 'abort';
    }

    if (code.startsWith('license_')) {
      await this.releaseInflight(ctx.inflight, code, false);
      await this.applyReadOnly(true, code);
      this.scheduleReadOnlyProbe();
      return 'readonly';
    }

    switch (code) {
      case 'activation_revoked':
        await this.releaseInflight(ctx.inflight, code, false);
        await this.enterState('revoked', code);
        return 'abort';
      case 'sync_not_enabled':
      case 'sync_disabled':
        await this.releaseInflight(ctx.inflight, code, false);
        await this.enterState('disabled', code);
        return 'abort';
      case 'client_upgrade_required':
        await this.releaseInflight(ctx.inflight, code, false);
        await this.enterState('paused_upgrade', code);
        return 'abort';
      case 'batch_invalid':
        return 'isolate';
      case 'cursor_expired':
        return 'rebootstrap_cursor';
      case 'sync_epoch_changed':
        return 'rebootstrap_epoch';
      default:
        break;
    }

    // Transitoire (429, 5xx, réponse mal formée) : reprise avec délai croissant, jamais d'abandon.
    if (err.retry === 'transient' || err.statusCode === 429 || (err.statusCode ?? 0) >= 500 || err.kind === 'protocol') {
      await this.releaseInflight(ctx.inflight, code || `http_${err.statusCode ?? 'x'}`, true, err.retryAfterMs ?? 0);
      this.registerGlobalFailure(code || `http_${err.statusCode ?? 'x'}`, err.retryAfterMs ?? 0);
      return 'abort';
    }

    // Refus de requête inconnu et définitif : on s'arrête sans toucher à la file ; nouvel essai lent.
    await this.releaseInflight(ctx.inflight, code || 'request_refused', false);
    this.registerGlobalFailure(code || 'request_refused', 0);
    return 'abort';
  }

  private registerGlobalFailure(code: string, retryAfterMs: number): void {
    this.consecutiveFailures += 1;
    this.lastErrorCode = code;
    this.state = 'error';
    const delay = backoffDelayMs(this.consecutiveFailures, this.random, retryAfterMs);
    this.nextAllowedAt = this.now() + delay;
    this.scheduleAt(delay);
  }

  // --- envoi ---------------------------------------------------------------------------------------

  private async pushLoop(summary: CycleSummary): Promise<'ok' | 'abort'> {
    for (let i = 0; i < MAX_PUSH_ITERATIONS; i += 1) {
      const entries = await this.deps.outbox.claimDue(SYNC_LIMITS.maxOpsPerPush, SYNC_LIMITS.maxPushBytes);
      if (entries.length === 0) return 'ok';
      const outcome = await this.sendBatch(entries, summary);
      if (outcome === 'abort') return 'abort';
      if (outcome === 'readonly') return 'ok';
    }
    return 'ok';
  }

  private async sendBatch(entries: SyncWireEntry[], summary: CycleSummary): Promise<'ok' | 'abort' | 'readonly'> {
    const local = await this.deps.outbox.getState();
    const device = this.deps.deviceLabel ? await this.deps.deviceLabel() : undefined;
    let response: SyncPushResponse;
    try {
      response = await this.deps.api.push({
        protocol: SYNC_PROTOCOL_VERSION,
        schema_version: SYNC_CLIENT_SCHEMA_VERSION,
        app_version: this.deps.appVersion,
        ...(device ? { device_label: device } : {}),
        pending_count: local.pendingCount,
        oldest_pending_at: local.oldestPendingAt,
        ops: entries.map(toWireOp),
      });
    } catch (err) {
      const disposition = await this.onRequestError(err, { inflight: entries, where: 'push' });
      if (disposition === 'isolate') {
        if (entries.length === 1) {
          const only = entries[0]!;
          const message = rejectionMessageFr('batch_invalid');
          await this.deps.outbox.markRejected(only.op_id, 'batch_invalid', message);
          summary.rejected += 1;
          this.deps.onNotice?.({ kind: 'entry_rejected', code: 'batch_invalid', resource: only.resource, message });
          return 'ok';
        }
        // Isolement de l'entrée fautive : une par une.
        await this.deps.outbox.markRetry(
          entries.map((e) => ({ op_id: e.op_id, delay_ms: 0 })),
          'batch_invalid',
          false,
        );
        for (let k = 0; k < entries.length; k += 1) {
          const claimed = await this.deps.outbox.claimDue(1, SYNC_LIMITS.maxPushBytes);
          if (claimed.length === 0) break;
          const out = await this.sendBatch(claimed, summary);
          if (out !== 'ok') return out;
        }
        return 'ok';
      }
      if (disposition === 'retry_now') return 'ok';
      if (disposition === 'readonly') return 'readonly';
      if (disposition === 'rebootstrap_epoch' || disposition === 'rebootstrap_cursor') {
        const status = await this.deps.api.status().catch(() => null);
        if (!status) return 'abort';
        const outcome = await this.rebootstrap(disposition === 'rebootstrap_epoch' ? 'epoch_changed' : 'cursor_expired', status);
        return outcome === 'abort' ? 'abort' : 'ok';
      }
      return 'abort';
    }
    this.monitor.reportSuccess();
    await this.processResults(entries, response, summary);
    return 'ok';
  }

  private async processResults(entries: SyncWireEntry[], response: SyncPushResponse, summary: CycleSummary): Promise<void> {
    const byOp = new Map(entries.map((e) => [e.op_id, e]));
    const acks: Array<{ op_id: string; entity?: NonNullable<SyncOpResult['entity']>; payload?: Record<string, unknown> }> = [];
    const retries = new Map<string, SyncRetryItem[]>();
    const answered = new Set<string>();
    for (const result of response.results) {
      const entry = byOp.get(result.op_id);
      if (!entry) continue;
      answered.add(result.op_id);
      if (result.status !== 'rejected') {
        acks.push({ op_id: result.op_id, ...(result.entity ? { entity: result.entity } : {}), ...(result.payload ? { payload: result.payload } : {}) });
        continue;
      }
      const error = result.error ?? { code: 'rejected' };
      if (error.retry === 'transient') {
        const list = retries.get(error.code) ?? [];
        list.push({ op_id: result.op_id, delay_ms: backoffDelayMs(entry.attempts + 1, this.random) });
        retries.set(error.code, list);
        summary.retried += 1;
        continue;
      }
      // Permanent, en conflit ou sans classe : conservé en `rejected` (jamais supprimé).
      const message = rejectionMessageFr(error.code, error.message);
      await this.deps.outbox.markRejected(result.op_id, error.code, message, result.entity);
      summary.rejected += 1;
      if (error.code === 'payslip_locked') this.deps.onNotice?.({ kind: 'bulletin_locked', message });
      else this.deps.onNotice?.({ kind: 'entry_rejected', code: error.code, resource: entry.resource, message });
    }
    if (acks.length > 0) {
      const { merged } = await this.deps.outbox.ack(acks);
      summary.pushed += acks.length;
      if (merged.length > 0) this.deps.onNotice?.({ kind: 'merged_from_other_device', count: merged.length });
    }
    for (const [code, items] of retries) await this.deps.outbox.markRetry(items, code, true);
    // Une opération absente de la réponse n'a pas de résultat connu : on la rejoue plus tard (idempotent).
    const missing = entries.filter((e) => !answered.has(e.op_id));
    if (missing.length > 0) await this.releaseInflight(missing, 'missing_result', true);
  }

  // --- réception -------------------------------------------------------------------------------------

  private async pullLoop(summary: CycleSummary): Promise<'ok' | 'abort'> {
    for (let page = 0; page < 10_000; page += 1) {
      const local = await this.deps.outbox.getState();
      let response;
      try {
        response = await this.deps.api.pull({ cursor: local.cursor, limit: SYNC_LIMITS.maxPullPage });
      } catch (err) {
        const disposition = await this.onRequestError(err, { inflight: [], where: 'pull' });
        if (disposition === 'retry_now') {
          page -= 1;
          continue;
        }
        if (disposition === 'rebootstrap_cursor' || disposition === 'rebootstrap_epoch') {
          const status = await this.deps.api.status().catch(() => null);
          if (!status) return 'abort';
          const out = await this.rebootstrap(disposition === 'rebootstrap_epoch' ? 'epoch_changed' : 'cursor_expired', status);
          if (out === 'abort') return 'abort';
          continue;
        }
        return disposition === 'readonly' ? 'ok' : 'abort';
      }
      this.monitor.reportSuccess();
      const cursorEpoch = parseCursor(local.cursor)?.epoch ?? local.epoch;
      if (cursorEpoch && response.epoch !== cursorEpoch && local.phase === 'live' && local.cursor) {
        const status = await this.deps.api.status().catch(() => null);
        if (!status) return 'abort';
        const out = await this.rebootstrap('epoch_changed', status);
        if (out === 'abort') return 'abort';
        continue;
      }
      await this.applyPage(response.items, response.next_cursor, response.epoch, summary);
      if (response.denied_resources && response.denied_resources.length > 0) {
        await this.safeEvent({ type: 'sync_pull_applied', code: 'denied_resources', n: response.denied_resources.length });
      }
      if (!response.has_more) return 'ok';
    }
    return 'ok';
  }

  /** Applique une page par paquets (interface jamais figée) ; le curseur n'avance qu'avec le dernier paquet. */
  private async applyPage(
    items: Parameters<SyncOutboxPort['applyRemoteBatch']>[0],
    nextCursor: string,
    epoch: string,
    summary: CycleSummary,
  ): Promise<void> {
    const totals: Record<string, number> = {};
    const chunk = SYNC_LIMITS.applyChunk;
    if (items.length === 0) {
      await this.deps.outbox.applyRemoteBatch([], { nextCursor, epoch });
      return;
    }
    for (let i = 0; i < items.length; i += chunk) {
      const last = i + chunk >= items.length;
      const part = items.slice(i, i + chunk);
      const res = await this.deps.outbox.applyRemoteBatch(part, last ? { nextCursor, epoch } : { epoch: null });
      for (const [resource, n] of Object.entries(res.applied)) totals[resource] = (totals[resource] ?? 0) + n;
      summary.pulled += Object.values(res.applied).reduce((a, b) => a + b, 0);
      if (!last) await this.deps.yieldToUi?.();
    }
    if (Object.keys(totals).length > 0) {
      await this.safeEvent({ type: 'sync_pull_applied', n: Object.values(totals).reduce((a, b) => a + b, 0) });
      this.deps.onRemoteApplied?.(totals);
    }
  }

  // --- premier envoi, bootstrap, rebootstrap ------------------------------------------------------

  private async runInitial(status: SyncStatusResponse, readOnly: boolean, summary: CycleSummary): Promise<'ok' | 'abort'> {
    try {
      const outcome = await this.initial.run({ status, readOnly });
      summary.pulled += outcome.downloaded;
      if (outcome.waitingForWritableLicence) return 'ok';
      this.deps.onNotice?.({
        kind: 'initial_summary',
        downloaded: outcome.downloaded,
        replaced: outcome.replaced,
        uploaded: outcome.uploaded,
        identical: outcome.identical,
      });
      await this.safeEvent({ type: 'sync_bootstrap', n: outcome.downloaded + outcome.uploaded });
      if (outcome.downloaded > 0) this.deps.onRemoteApplied?.({ bootstrap: outcome.downloaded });
      return 'ok';
    } catch (err) {
      const disposition = await this.onRequestError(err, { inflight: [], where: 'bootstrap' });
      return disposition === 'readonly' || disposition === 'retry_now' ? 'ok' : 'abort';
    }
  }

  private async rebootstrap(mode: 'cursor_expired' | 'epoch_changed', status: SyncStatusResponse): Promise<'ok' | 'abort'> {
    this.deps.onNotice?.({ kind: 'rebootstrap', reason: mode });
    await this.safeEvent({ type: 'sync_cursor_expired', code: mode });
    try {
      await this.initial.rebootstrap(mode, status);
      return 'ok';
    } catch (err) {
      const disposition = await this.onRequestError(err, { inflight: [], where: 'bootstrap' });
      return disposition === 'readonly' || disposition === 'retry_now' ? 'ok' : 'abort';
    }
  }

  // --- planification ------------------------------------------------------------------------------

  private schedulePeriodic(): void {
    this.clearPeriodic();
    if (!this.started) return;
    if (this.state === 'offline' || this.state === 'revoked' || this.state === 'disabled' || this.state === 'paused_upgrade' || this.state === 'needs_login') {
      return;
    }
    if (this.state === 'error') return; // la reprise est planifiée par registerGlobalFailure
    const base = this.state === 'read_only' ? READ_ONLY_PROBE_MS : PERIODIC_MS;
    const delay = Math.round(base * (1 + (this.random() * 2 - 1) * 0.1));
    this.timerHandle = this.timers.set(() => {
      this.timerHandle = null;
      void this.runCycle('timer');
    }, delay);
  }

  private scheduleAt(delayMs: number): void {
    this.clearPeriodic();
    this.timerHandle = this.timers.set(() => {
      this.timerHandle = null;
      void this.runCycle('timer');
    }, delayMs);
  }

  private scheduleQuickRetry(): void {
    this.scheduleAt(QUICK_RETRY_MS);
  }

  private scheduleReadOnlyProbe(): void {
    this.scheduleAt(READ_ONLY_PROBE_MS);
  }

  private clearPeriodic(): void {
    if (this.timerHandle !== null) this.timers.clear(this.timerHandle);
    this.timerHandle = null;
  }

  private clearTimers(): void {
    this.clearPeriodic();
    if (this.debounceHandle !== null) this.timers.clear(this.debounceHandle);
    this.debounceHandle = null;
  }

  // --- état exposé ----------------------------------------------------------------------------------

  private async refreshCounters(): Promise<void> {
    try {
      this.counters = await this.deps.outbox.getState();
      this.attentionCount = this.counters.rejectedCount + this.counters.quarantineCount;
    } catch {
      /* le compteur garde sa dernière valeur */
    }
  }

  private buildStatus(): SyncStatus {
    const c = this.counters;
    const lastSuccess = c?.lastSuccessAt ?? (this.lastSuccessMs ? new Date(this.lastSuccessMs).toISOString() : null);
    let blockedDays = 0;
    if (c && c.pendingCount > 0 && c.oldestPendingAt) {
      const reference = lastSuccess ? Date.parse(lastSuccess) : Date.parse(c.oldestPendingAt);
      const idle = this.now() - reference;
      if (idle > BLOCKED_AFTER_MS) blockedDays = Math.floor(idle / 86_400_000);
    }
    const offlineSince = this.monitor.offlineSince;
    return {
      state: this.state,
      phase: c?.phase ?? 'uninitialized',
      pendingCount: c?.pendingCount ?? 0,
      retryCount: c?.retryCount ?? 0,
      rejectedCount: c?.rejectedCount ?? 0,
      attentionCount: this.attentionCount,
      oldestPendingAt: c?.oldestPendingAt ?? null,
      lastSuccessAt: lastSuccess,
      lastPullAt: c?.lastPullAt ?? null,
      readOnlyReason: this.readOnlyReason,
      offlineSince: offlineSince === null ? null : new Date(offlineSince).toISOString(),
      lastErrorCode: this.lastErrorCode,
      blockedDays,
      progress: this.progress,
    };
  }

  private publish(): void {
    this.deps.onStatus?.(this.buildStatus());
  }

  private async safeEvent(event: Parameters<SyncOutboxPort['recordEvent']>[0]): Promise<void> {
    try {
      await this.deps.outbox.recordEvent(event);
    } catch {
      /* le journal ne doit jamais gêner la synchronisation */
    }
  }
}

const INSTALLATION_CODES = new Set(['installation_invalid', 'installation_token_required', 'installation_expired']);

function emptySummary(state: SyncEngineState): CycleSummary {
  return { pushed: 0, rejected: 0, retried: 0, pulled: 0, skipped: false, state };
}
